import { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Users,
  MessageSquareText,
  Activity as ActivityIcon,
  CheckSquare,
  Bell,
  FileText,
  Handshake,
  Clock,
  Plus,
  Trash2,
  Pencil,
  Search,
  UserPlus,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import QuickAddDialog from "@/components/QuickAddDialog";
import { PrimaryButton } from "@/components/Shared";

const STATUS_COLOR = {
  interessent: "bg-amber-50 text-amber-700 border-amber-200",
  kunde: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partner: "bg-violet-50 text-violet-700 border-violet-200",
  inaktiv: "bg-gray-100 text-gray-500 border-gray-200",
};
const STATUS_LABEL = {
  interessent: "Interessent",
  kunde: "Kunde",
  partner: "Partner",
  inaktiv: "Inaktiv",
};

const KIND_META = {
  activity: { icon: ActivityIcon, label: "Aktivität", color: "text-blue-700 bg-blue-50" },
  note: { icon: MessageSquareText, label: "Gespräch", color: "text-amber-700 bg-amber-50" },
  task: { icon: CheckSquare, label: "Aufgabe", color: "text-emerald-700 bg-emerald-50" },
  followup: { icon: Bell, label: "Wiedervorlage", color: "text-rose-700 bg-rose-50" },
  quote: { icon: FileText, label: "Angebot", color: "text-violet-700 bg-violet-50" },
};

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        <div className="text-sm text-gray-900 break-words" data-testid={`info-${label.toLowerCase()}`}>{value}</div>
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  if (!value) return null;
  return (
    <span
      data-testid="company-status-pill"
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium border ${STATUS_COLOR[value] || "bg-gray-100 text-gray-600 border-gray-200"}`}
    >
      {STATUS_LABEL[value] || value}
    </span>
  );
}

function EmptyHint({ text }) {
  return <div className="text-sm text-gray-400 py-8 text-center" data-testid="empty-hint">{text}</div>;
}

function formatAddress(c) {
  const line1 = [c.street, c.house_number].filter(Boolean).join(" ");
  const line2 = [c.postal_code, c.city].filter(Boolean).join(" ");
  const parts = [line1, line2, c.country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeQuickAdd, setActiveQuickAdd] = useState(null);
  const [partnerSelect, setPartnerSelect] = useState("");
  const [partnerRelation, setPartnerRelation] = useState("");
  const [confirmDeleteCompany, setConfirmDeleteCompany] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [contactAddMode, setContactAddMode] = useState(null); // null | "choice" | "new" | "existing"
  const [existingContactId, setExistingContactId] = useState("");
  const [existingContactSearch, setExistingContactSearch] = useState("");
  const [reassignConfirm, setReassignConfirm] = useState(null); // { contact, currentCompanyName }

  const { data: dossier, isLoading, error } = useQuery({
    queryKey: ["dossier", companyId],
    queryFn: async () => (await api.get(`/companies/${companyId}/dossier`)).data,
    enabled: !!companyId,
  });

  const { data: allPartners = [] } = useQuery({
    queryKey: ["partners", "all"],
    queryFn: async () => (await api.get("/partners")).data,
  });

  const linkPartnerMut = useMutation({
    mutationFn: async () => {
      if (!partnerSelect) throw new Error("Bitte wähle einen Partner aus.");
      const { data } = await api.post(`/companies/${companyId}/partners`, {
        partner_id: partnerSelect,
        relation_type: partnerRelation || null,
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Partner verknüpft");
      setPartnerSelect("");
      setPartnerRelation("");
      qc.invalidateQueries({ queryKey: ["dossier", companyId] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const unlinkPartnerMut = useMutation({
    mutationFn: async (linkId) => (await api.delete(`/companies/${companyId}/partners/${linkId}`)).data,
    onSuccess: () => {
      toast.success("Verknüpfung entfernt");
      qc.invalidateQueries({ queryKey: ["dossier", companyId] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const deleteCompanyMut = useMutation({
    mutationFn: async () => (await api.delete(`/companies/${companyId}`)).data,
    onSuccess: () => {
      toast.success("Firma gelöscht");
      navigate("/companies", { replace: true });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const linkedPartnerIds = useMemo(
    () => new Set((dossier?.partners || []).map((p) => p.partner_id)),
    [dossier?.partners],
  );
  const availablePartners = useMemo(
    () => allPartners.filter((p) => !linkedPartnerIds.has(p.id)),
    [allPartners, linkedPartnerIds],
  );

  if (isLoading) {
    return <div className="p-10 text-sm text-gray-500" data-testid="dossier-loading">Lädt Firmenakte…</div>;
  }
  if (error || !dossier) {
    return (
      <div className="p-10 text-sm text-rose-600" data-testid="dossier-error">
        Firma nicht gefunden.{" "}
        <Link to="/companies" className="underline">Zurück zur Liste</Link>
      </div>
    );
  }

  const c = dossier.company;
  const addr = formatAddress(c);

  // ---- Quick-add field configs ----
  const quickAddConfig = {
    contact: {
      title: "Person anlegen",
      resource: "contacts",
      submitButtonLabel: "Person speichern",
      successMessage: "Person gespeichert",
      fields: [
        { name: "first_name", label: "Vorname", type: "text", required: true },
        { name: "last_name", label: "Nachname", type: "text", required: true },
        { name: "position", label: "Position", type: "text" },
        { name: "email", label: "E-Mail", type: "email" },
        { name: "phone", label: "Telefon", type: "tel" },
      ],
      lockedFields: { company_id: companyId },
      testidPrefix: "qa-contact",
    },
    note: {
      title: "Gespräch erfassen",
      resource: "notes",
      submitButtonLabel: "Gespräch speichern",
      successMessage: "Gespräch gespeichert",
      fields: [
        { name: "title", label: "Titel", type: "text", required: true },
        { name: "content", label: "Inhalt", type: "textarea", required: true, rows: 5, fullWidth: true },
      ],
      lockedFields: { company_id: companyId },
      testidPrefix: "qa-note",
    },
    activity: {
      title: "Aktivität erfassen",
      resource: "activities",
      submitButtonLabel: "Aktivität speichern",
      successMessage: "Aktivität gespeichert",
      defaults: { type: "call" },
      fields: [
        { name: "subject", label: "Betreff", type: "text", required: true },
        {
          name: "type",
          label: "Typ",
          type: "select",
          required: true,
          options: [
            { value: "call", label: "Anruf" },
            { value: "email", label: "E-Mail" },
            { value: "meeting", label: "Meeting" },
            { value: "note", label: "Notiz" },
            { value: "task", label: "Aufgabe" },
          ],
        },
        { name: "occurred_at", label: "Datum/Zeit", type: "datetime" },
        { name: "description", label: "Beschreibung", type: "textarea", fullWidth: true, rows: 3 },
      ],
      lockedFields: { company_id: companyId },
      testidPrefix: "qa-activity",
    },
    task: {
      title: "Aufgabe anlegen",
      resource: "tasks",
      submitButtonLabel: "Aufgabe speichern",
      successMessage: "Aufgabe gespeichert",
      defaults: { status: "open", priority: "medium" },
      fields: [
        { name: "title", label: "Titel", type: "text", required: true },
        {
          name: "status",
          label: "Status",
          type: "select",
          options: [
            { value: "open", label: "Offen" },
            { value: "in_progress", label: "In Arbeit" },
            { value: "done", label: "Erledigt" },
          ],
        },
        {
          name: "priority",
          label: "Priorität",
          type: "select",
          options: [
            { value: "low", label: "Niedrig" },
            { value: "medium", label: "Mittel" },
            { value: "high", label: "Hoch" },
          ],
        },
        { name: "due_date", label: "Fällig am", type: "datetime" },
        { name: "description", label: "Beschreibung", type: "textarea", fullWidth: true, rows: 3 },
      ],
      lockedFields: { company_id: companyId },
      testidPrefix: "qa-task",
    },
    followup: {
      title: "Wiedervorlage anlegen",
      resource: "followups",
      submitButtonLabel: "Wiedervorlage speichern",
      successMessage: "Wiedervorlage gespeichert",
      defaults: { status: "open" },
      fields: [
        { name: "title", label: "Titel", type: "text", required: true },
        { name: "due_date", label: "Fällig am", type: "datetime", required: true },
        { name: "description", label: "Beschreibung", type: "textarea", fullWidth: true, rows: 3 },
      ],
      lockedFields: { company_id: companyId },
      testidPrefix: "qa-followup",
    },
    quote: {
      title: "Angebot erstellen",
      resource: "quotes",
      submitButtonLabel: "Angebot speichern",
      successMessage: "Angebot gespeichert",
      defaults: { status: "draft", currency: "EUR" },
      fields: [
        { name: "quote_number", label: "Angebotsnr.", type: "text", required: true, placeholder: "z. B. ANG-2026-001" },
        { name: "title", label: "Titel", type: "text", required: true },
        { name: "amount", label: "Betrag", type: "number", step: "0.01" },
        { name: "valid_until", label: "Gültig bis", type: "date" },
      ],
      lockedFields: { company_id: companyId },
      testidPrefix: "qa-quote",
    },
  };

  const config = quickAddConfig[activeQuickAdd];

  return (
    <div data-testid="company-detail-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/companies")}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 mb-2"
            data-testid="company-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Firmen
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-bold tracking-tight text-3xl text-gray-900" data-testid="company-name-heading">
              {c.name}
            </h1>
            <StatusPill value={c.status} />
          </div>
          {c.industry ? (
            <div className="text-sm text-gray-500 mt-1">{c.industry}</div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setEditOpen(true)}
            data-testid="company-edit-btn"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Bearbeiten
          </Button>
          <Button
            variant="outline"
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            onClick={() => setConfirmDeleteCompany(true)}
            data-testid="company-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1.5" /> Löschen
          </Button>
        </div>
      </div>

      {/* Stammdaten + Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 crm-card p-5" data-testid="company-stammdaten">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-gray-500" />
            <h2 className="font-display font-semibold text-gray-900">Stammdaten</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <InfoRow icon={Mail} label="E-Mail" value={c.email} />
            <InfoRow icon={Phone} label="Telefon" value={c.phone} />
            <InfoRow icon={Globe} label="Website" value={c.website} />
            <InfoRow icon={MapPin} label="Adresse" value={addr} />
            <InfoRow
              icon={Users}
              label="Hauptansprechpartner"
              value={c.primary_contact_name}
            />
          </div>
        </div>

        <div className="crm-card p-5">
          <h2 className="font-display font-semibold text-gray-900 mb-3">Auf einen Blick</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center justify-between"><span className="text-gray-500">Personen</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-contacts">{dossier.contacts.length}</span></li>
            <li className="flex items-center justify-between"><span className="text-gray-500">Aktivitäten</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-activities">{dossier.activities.length}</span></li>
            <li className="flex items-center justify-between"><span className="text-gray-500">Gespräche</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-notes">{dossier.notes.length}</span></li>
            <li className="flex items-center justify-between"><span className="text-gray-500">Aufgaben</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-tasks">{dossier.tasks.length}</span></li>
            <li className="flex items-center justify-between"><span className="text-gray-500">Wiedervorlagen</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-followups">{dossier.followups.length}</span></li>
            <li className="flex items-center justify-between"><span className="text-gray-500">Angebote</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-quotes">{dossier.quotes.length}</span></li>
            <li className="flex items-center justify-between"><span className="text-gray-500">Partner</span><span className="font-medium text-gray-900 tabular-nums" data-testid="count-partners">{dossier.partners.length}</span></li>
          </ul>
        </div>
      </div>

      {/* Schnellzugriff */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="quick-actions">
        <span className="text-xs uppercase tracking-wider text-gray-500 mr-2">Schnellzugriff</span>
        <PrimaryButton onClick={() => setContactAddMode("choice")} data-testid="qa-open-contact-btn">
          <Plus className="w-4 h-4 mr-1" /> Person
        </PrimaryButton>
        <PrimaryButton onClick={() => setActiveQuickAdd("activity")} data-testid="qa-open-activity-btn">
          <Plus className="w-4 h-4 mr-1" /> Aktivität
        </PrimaryButton>
        <PrimaryButton onClick={() => setActiveQuickAdd("task")} data-testid="qa-open-task-btn">
          <Plus className="w-4 h-4 mr-1" /> Aufgabe
        </PrimaryButton>
        <Button variant="outline" onClick={() => setActiveQuickAdd("note")} data-testid="qa-open-note-btn"><Plus className="w-4 h-4 mr-1" />Gespräch</Button>
        <Button variant="outline" onClick={() => setActiveQuickAdd("followup")} data-testid="qa-open-followup-btn"><Plus className="w-4 h-4 mr-1" />Wiedervorlage</Button>
        <Button variant="outline" onClick={() => setActiveQuickAdd("quote")} data-testid="qa-open-quote-btn"><Plus className="w-4 h-4 mr-1" />Angebot</Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="timeline" className="w-full">
        <TabsList className="flex flex-wrap h-auto justify-start bg-gray-100 p-1" data-testid="company-tabs">
          <TabsTrigger value="timeline" data-testid="tab-timeline"><Clock className="w-3.5 h-3.5 mr-1.5" />Historie</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Personen ({dossier.contacts.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Gespräche ({dossier.notes.length})</TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activities">Aktivitäten ({dossier.activities.length})</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Aufgaben ({dossier.tasks.length})</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups">Wiedervorlagen ({dossier.followups.length})</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes">Angebote ({dossier.quotes.length})</TabsTrigger>
          <TabsTrigger value="partners" data-testid="tab-partners">Partner ({dossier.partners.length})</TabsTrigger>
        </TabsList>

        {/* Timeline */}
        <TabsContent value="timeline" className="mt-4">
          <div className="crm-card p-5" data-testid="timeline-panel">
            <h3 className="font-display font-semibold text-gray-900 mb-4">Chronologische Historie</h3>
            {dossier.timeline.length === 0 ? (
              <EmptyHint text="Noch keine Aktivitäten, Notizen oder Aufgaben für diese Firma." />
            ) : (
              <ol className="relative border-l border-gray-200 ml-3 space-y-5">
                {dossier.timeline.map((item) => {
                  const Meta = KIND_META[item.kind] || KIND_META.activity;
                  const Icon = Meta.icon;
                  return (
                    <li key={`${item.kind}-${item.id}`} className="ml-6" data-testid={`timeline-item-${item.kind}-${item.id}`}>
                      <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white ${Meta.color}`}>
                        <Icon className="w-3 h-3" />
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs uppercase tracking-wider text-gray-500">{Meta.label}</span>
                        <time className="text-xs text-gray-400">
                          {new Date(item.occurred_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
                        </time>
                        {item.meta ? (
                          <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{item.meta}</span>
                        ) : null}
                      </div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">{item.title}</div>
                      {item.description ? (
                        <div className="text-sm text-gray-600 mt-0.5 line-clamp-3">{item.description}</div>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </TabsContent>

        {/* Contacts */}
        <TabsContent value="contacts" className="mt-4">
          <div className="crm-card p-5" data-testid="contacts-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-gray-900">Personen</h3>
              <PrimaryButton onClick={() => setContactAddMode("choice")} data-testid="contacts-panel-add"><Plus className="w-4 h-4 mr-1" />Person</PrimaryButton>
            </div>
            {dossier.contacts.length === 0 ? (
              <EmptyHint text="Noch keine Personen dieser Firma zugeordnet." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.contacts.map((ct) => (
                  <li key={ct.id} className="py-3 flex items-start justify-between gap-4" data-testid={`contact-item-${ct.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{ct.first_name} {ct.last_name}</div>
                      <div className="text-xs text-gray-500">{ct.position || ""}</div>
                      <div className="text-xs text-gray-500">
                        {ct.email ? <span className="mr-3">{ct.email}</span> : null}
                        {ct.phone || ct.mobile ? <span>{ct.phone || ct.mobile}</span> : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" className="mt-4">
          <div className="crm-card p-5" data-testid="notes-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-gray-900">Gespräche</h3>
              <PrimaryButton onClick={() => setActiveQuickAdd("note")} data-testid="notes-panel-add"><Plus className="w-4 h-4 mr-1" />Gespräch</PrimaryButton>
            </div>
            {dossier.notes.length === 0 ? (
              <EmptyHint text="Noch keine Gespräche festgehalten." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.notes.map((n) => (
                  <li key={n.id} className="py-3" data-testid={`note-item-${n.id}`}>
                    <div className="text-sm font-medium text-gray-900">{n.title}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{new Date(n.created_at).toLocaleString("de-DE")}</div>
                    <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{n.content}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Activities */}
        <TabsContent value="activities" className="mt-4">
          <div className="crm-card p-5" data-testid="activities-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-gray-900">Aktivitäten</h3>
              <PrimaryButton onClick={() => setActiveQuickAdd("activity")} data-testid="activities-panel-add"><Plus className="w-4 h-4 mr-1" />Aktivität</PrimaryButton>
            </div>
            {dossier.activities.length === 0 ? (
              <EmptyHint text="Noch keine Aktivitäten festgehalten." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.activities.map((a) => (
                  <li key={a.id} className="py-3" data-testid={`activity-item-${a.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{a.type}</span>
                      <time className="text-xs text-gray-400">{new Date(a.occurred_at).toLocaleString("de-DE")}</time>
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">{a.subject}</div>
                    {a.description ? <div className="text-sm text-gray-600 mt-0.5">{a.description}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Tasks */}
        <TabsContent value="tasks" className="mt-4">
          <div className="crm-card p-5" data-testid="tasks-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-gray-900">Aufgaben</h3>
              <PrimaryButton onClick={() => setActiveQuickAdd("task")} data-testid="tasks-panel-add"><Plus className="w-4 h-4 mr-1" />Aufgabe</PrimaryButton>
            </div>
            {dossier.tasks.length === 0 ? (
              <EmptyHint text="Noch keine offenen Aufgaben." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.tasks.map((t) => (
                  <li key={t.id} className="py-3 flex items-start justify-between gap-4" data-testid={`task-item-${t.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{t.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {t.due_date ? `Fällig ${new Date(t.due_date).toLocaleDateString("de-DE")}` : "Kein Fälligkeitsdatum"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{t.priority}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{t.status}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Followups */}
        <TabsContent value="followups" className="mt-4">
          <div className="crm-card p-5" data-testid="followups-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-gray-900">Wiedervorlagen</h3>
              <PrimaryButton onClick={() => setActiveQuickAdd("followup")} data-testid="followups-panel-add"><Plus className="w-4 h-4 mr-1" />Wiedervorlage</PrimaryButton>
            </div>
            {dossier.followups.length === 0 ? (
              <EmptyHint text="Noch keine Wiedervorlagen." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.followups.map((f) => (
                  <li key={f.id} className="py-3" data-testid={`followup-item-${f.id}`}>
                    <div className="text-sm font-medium text-gray-900">{f.title}</div>
                    <div className="text-xs text-gray-500">{new Date(f.due_date).toLocaleString("de-DE")} · {f.status}</div>
                    {f.description ? <div className="text-sm text-gray-600 mt-0.5">{f.description}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Quotes */}
        <TabsContent value="quotes" className="mt-4">
          <div className="crm-card p-5" data-testid="quotes-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-gray-900">Angebote</h3>
              <PrimaryButton onClick={() => setActiveQuickAdd("quote")} data-testid="quotes-panel-add"><Plus className="w-4 h-4 mr-1" />Angebot</PrimaryButton>
            </div>
            {dossier.quotes.length === 0 ? (
              <EmptyHint text="Noch keine Angebote angelegt." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.quotes.map((q) => (
                  <li key={q.id} className="py-3 flex items-start justify-between gap-4" data-testid={`quote-item-${q.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        <span className="font-mono text-xs text-gray-500 mr-2">{q.quote_number}</span>
                        {q.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{q.status} {q.valid_until ? `· gültig bis ${new Date(q.valid_until).toLocaleDateString("de-DE")}` : ""}</div>
                    </div>
                    <div className="text-sm font-medium tabular-nums text-gray-900">
                      {q.amount ? new Intl.NumberFormat("de-DE", { style: "currency", currency: q.currency || "EUR" }).format(Number(q.amount)) : "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Partners */}
        <TabsContent value="partners" className="mt-4">
          <div className="crm-card p-5" data-testid="partners-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-display font-semibold text-gray-900 flex items-center gap-2">
                <Handshake className="w-4 h-4" />Partner-Verknüpfungen
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={partnerSelect} onValueChange={setPartnerSelect}>
                  <SelectTrigger className="w-56" data-testid="partner-select-trigger">
                    <SelectValue placeholder="Partner wählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePartners.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Keine weiteren Partner verfügbar</div>
                    ) : (
                      availablePartners.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}{p.type ? ` · ${p.type}` : ""}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <input
                  type="text"
                  className="h-9 px-3 rounded-md border border-gray-200 text-sm w-44"
                  placeholder="Beziehungsart (opt.)"
                  value={partnerRelation}
                  onChange={(e) => setPartnerRelation(e.target.value)}
                  data-testid="partner-relation-input"
                />
                <PrimaryButton
                  onClick={() => linkPartnerMut.mutate()}
                  disabled={!partnerSelect || linkPartnerMut.isPending}
                  data-testid="partner-link-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Verknüpfen
                </PrimaryButton>
              </div>
            </div>
            {dossier.partners.length === 0 ? (
              <EmptyHint text="Noch keine Partner verknüpft." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.partners.map((p) => (
                  <li key={p.id} className="py-3 flex items-start justify-between gap-4" data-testid={`partner-link-${p.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{p.partner_name}</div>
                      <div className="text-xs text-gray-500">
                        {p.partner_type ? <span className="mr-3">{p.partner_type}</span> : null}
                        {p.relation_type ? <span>Beziehung: {p.relation_type}</span> : null}
                      </div>
                    </div>
                    <button
                      onClick={() => unlinkPartnerMut.mutate(p.id)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded-md"
                      data-testid={`partner-unlink-${p.id}`}
                      aria-label="Verknüpfung entfernen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Quick add dialog */}
      {config ? (
        <QuickAddDialog
          open={!!activeQuickAdd}
          onOpenChange={(v) => !v && setActiveQuickAdd(null)}
          title={config.title}
          resource={config.resource}
          fields={config.fields}
          defaults={config.defaults || {}}
          lockedFields={config.lockedFields}
          submitButtonLabel={config.submitButtonLabel}
          successMessage={config.successMessage}
          invalidateQueryKeys={[["dossier", companyId]]}
          testidPrefix={config.testidPrefix}
        />
      ) : null}

      {/* Delete company confirm */}
      <AlertDialog open={confirmDeleteCompany} onOpenChange={setConfirmDeleteCompany}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Firma wirklich löschen?</AlertDialogTitle>
          <AlertDialogDescription>
            Die Firma „{c.name}“ und alle Partner-Verknüpfungen werden entfernt. Personen, Gespräche und Aufgaben bleiben erhalten (die Firmenzuordnung wird zurückgesetzt).
          </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => deleteCompanyMut.mutate()}
              data-testid="company-delete-confirm"
            >
              Firma löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit company dialog (Bug fix A) */}
      <CompanyEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        company={c}
        onSaved={() => qc.invalidateQueries({ queryKey: ["dossier", companyId] })}
      />

      {/* Kontakt-Auswahl-Dialog (D) */}
      <ContactAddChoiceDialog
        open={contactAddMode === "choice"}
        onOpenChange={(v) => !v && setContactAddMode(null)}
        onNew={() => {
          setContactAddMode(null);
          setActiveQuickAdd("contact");
        }}
        onExisting={() => {
          setContactAddMode("existing");
          setExistingContactId("");
          setExistingContactSearch("");
        }}
      />
      <ExistingContactPickerDialog
        open={contactAddMode === "existing"}
        onOpenChange={(v) => !v && setContactAddMode(null)}
        companyId={companyId}
        companyName={c.name}
        onAssigned={() => {
          setContactAddMode(null);
          qc.invalidateQueries({ queryKey: ["dossier", companyId] });
        }}
        onNeedsConfirm={(payload) => setReassignConfirm(payload)}
        searchValue={existingContactSearch}
        onSearchChange={setExistingContactSearch}
        pickedId={existingContactId}
        onPick={setExistingContactId}
      />
      <ReassignConfirmDialog
        payload={reassignConfirm}
        onCancel={() => setReassignConfirm(null)}
        onConfirm={async () => {
          if (!reassignConfirm) return;
          try {
            await api.put(`/contacts/${reassignConfirm.contact.id}`, { company_id: companyId });
            toast.success("Person verschoben");
            setReassignConfirm(null);
            setContactAddMode(null);
            qc.invalidateQueries({ queryKey: ["dossier", companyId] });
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
      />
    </div>
  );
}

// ---------------- Edit Company Dialog ----------------

const STATUS_OPTIONS = [
  { value: "interessent", label: "Interessent" },
  { value: "kunde", label: "Kunde" },
  { value: "partner", label: "Partner" },
  { value: "inaktiv", label: "Inaktiv" },
];

const EMPTY_COMPANY = {
  name: "",
  status: "interessent",
  industry: "",
  email: "",
  phone: "",
  website: "",
  street: "",
  house_number: "",
  postal_code: "",
  city: "",
  country: "",
};

export function CompanyEditDialog({ open, onOpenChange, company, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [saving, setSaving] = useState(false);

  // Rehydrate from source when opening
  const openState = open;
  const [prevOpen, setPrevOpen] = useState(false);
  if (openState && !prevOpen) {
    setForm({
      name: company?.name || "",
      status: company?.status || "interessent",
      industry: company?.industry || "",
      email: company?.email || "",
      phone: company?.phone || "",
      website: company?.website || "",
      street: company?.street || "",
      house_number: company?.house_number || "",
      postal_code: company?.postal_code || "",
      city: company?.city || "",
      country: company?.country || "",
    });
    setPrevOpen(true);
  } else if (!openState && prevOpen) {
    setPrevOpen(false);
  }

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  async function lookupPLZ(plz) {
    const trimmed = (plz || "").trim();
    if (!/^\d{5}$/.test(trimmed)) return;
    try {
      const { data } = await api.get(`/postal-lookup/${trimmed}`);
      if (data?.city) setField("city", data.city);
      if (data?.country) setField("country", data.country);
    } catch {
      /* silent */
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error("Bitte gib den Firmennamen an.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.put(`/companies/${company.id}`, payload);
      toast.success("Firma gespeichert");
      onOpenChange(false);
      onSaved?.();
      qc.invalidateQueries({ queryKey: ["companies"] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-2xl" data-testid="company-edit-dialog">
        <DialogHeader>
          <DialogTitle>Firma bearbeiten</DialogTitle>
          <DialogDescription>{`Stammdaten von „${company?.name || ""}“ anpassen.`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="sm:col-span-2">
            <Label>Firmenname *</Label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="company-edit-name" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger data-testid="company-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Branche</Label>
            <Input value={form.industry} onChange={(e) => setField("industry", e.target.value)} data-testid="company-edit-industry" />
          </div>
          <div>
            <Label>E-Mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="company-edit-email" />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="company-edit-phone" />
          </div>
          <div className="sm:col-span-2">
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://…" data-testid="company-edit-website" />
          </div>
          <div>
            <Label>Straße</Label>
            <Input value={form.street} onChange={(e) => setField("street", e.target.value)} data-testid="company-edit-street" />
          </div>
          <div>
            <Label>Hausnummer</Label>
            <Input value={form.house_number} onChange={(e) => setField("house_number", e.target.value)} data-testid="company-edit-house-number" />
          </div>
          <div>
            <Label>PLZ</Label>
            <Input
              value={form.postal_code}
              onChange={(e) => {
                const v = e.target.value;
                setField("postal_code", v);
                if (/^\d{5}$/.test(v.trim())) lookupPLZ(v);
              }}
              onBlur={() => lookupPLZ(form.postal_code)}
              data-testid="company-edit-postal-code"
            />
          </div>
          <div>
            <Label>Ort</Label>
            <Input value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="company-edit-city" />
          </div>
          <div className="sm:col-span-2">
            <Label>Land</Label>
            <Select
              value={form.country || "Deutschland"}
              onValueChange={(v) => setField("country", v)}
            >
              <SelectTrigger className="mt-1" data-testid="company-edit-country">
                <SelectValue placeholder="Land wählen…" />
              </SelectTrigger>
              <SelectContent>
                {["Deutschland","Österreich","Schweiz","Niederlande","Belgien","Frankreich","Italien","Spanien","Polen","Tschechien","Dänemark","Luxemburg","USA","Vereinigtes Königreich"].map((co) => (
                  <SelectItem key={co} value={co}>{co}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="company-edit-cancel"
          >
            Abbrechen
          </Button>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={handleSave}
            disabled={saving}
            data-testid="company-edit-save"
          >
            {saving ? "Speichern…" : "Firma speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Kontakt-Auswahl (D) ----------------

function ContactAddChoiceDialog({ open, onOpenChange, onNew, onExisting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contact-add-choice-dialog">
        <DialogHeader>
          <DialogTitle>Person zu dieser Firma hinzufügen</DialogTitle>
          <DialogDescription>Wähle, wie du fortfahren möchtest.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-2">
          <button
            type="button"
            className="text-left rounded-lg border border-gray-200 p-4 hover:border-gray-900 transition-colors"
            onClick={onNew}
            data-testid="contact-add-choice-new"
          >
            <div className="flex items-center gap-2 font-medium text-gray-900">
              <UserPlus className="w-4 h-4" /> Neue Person für diese Firma anlegen
            </div>
            <p className="text-xs text-gray-500 mt-1">Öffnet den Anlegen-Dialog mit fest zugeordneter Firma.</p>
          </button>
          <button
            type="button"
            className="text-left rounded-lg border border-gray-200 p-4 hover:border-gray-900 transition-colors"
            onClick={onExisting}
            data-testid="contact-add-choice-existing"
          >
            <div className="flex items-center gap-2 font-medium text-gray-900">
              <Search className="w-4 h-4" /> Bestehende Person zuordnen
            </div>
            <p className="text-xs text-gray-500 mt-1">Sucht in vorhandenen Personen und ordnet sie dieser Firma zu.</p>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExistingContactPickerDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  onAssigned,
  onNeedsConfirm,
  searchValue,
  onSearchChange,
  pickedId,
  onPick,
}) {
  const { data: allContacts = [] } = useQuery({
    queryKey: ["contacts", "picker"],
    queryFn: async () => (await api.get("/contacts")).data,
    enabled: open,
  });
  const { data: allCompaniesList = [] } = useQuery({
    queryKey: ["companies", "picker"],
    queryFn: async () => (await api.get("/companies")).data,
    enabled: open,
  });
  const companyNameById = useMemo(
    () => Object.fromEntries(allCompaniesList.map((c) => [c.id, c.name])),
    [allCompaniesList],
  );

  const q = (searchValue || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    const withoutSelf = allContacts.filter((c) => c.company_id !== companyId);
    if (!q) return withoutSelf.slice(0, 50);
    return withoutSelf
      .filter((c) => `${c.first_name} ${c.last_name} ${c.email || ""}`.toLowerCase().includes(q))
      .slice(0, 50);
  }, [allContacts, companyId, q]);

  async function handleAssign() {
    const contact = allContacts.find((c) => c.id === pickedId);
    if (!contact) {
      toast.error("Bitte wähle eine Person aus.");
      return;
    }
    if (contact.company_id && contact.company_id !== companyId) {
      onNeedsConfirm?.({
        contact,
        currentCompanyName: companyNameById[contact.company_id] || "einer anderen Firma",
      });
      return;
    }
    try {
      await api.put(`/contacts/${contact.id}`, { company_id: companyId });
      toast.success(`${contact.first_name} ${contact.last_name} ist jetzt „${companyName}“ zugeordnet.`);
      onAssigned?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="contact-existing-picker-dialog">
        <DialogHeader>
          <DialogTitle>Bestehende Person zuordnen</DialogTitle>
          <DialogDescription>
            Suche eine Person und ordne sie dieser Firma zu. Falls die Person bereits einer anderen Firma zugeordnet ist,
            wirst du gefragt, bevor eine Änderung übernommen wird.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Name oder E-Mail suchen…"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="contact-existing-search"
          />
          <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center">Keine Personen gefunden.</div>
            ) : (
              filtered.map((c) => {
                const currentCompany = c.company_id ? companyNameById[c.company_id] : null;
                const isSel = pickedId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onPick(c.id)}
                    className={`w-full text-left p-3 hover:bg-gray-50 ${isSel ? "bg-gray-100" : ""}`}
                    data-testid={`contact-existing-option-${c.id}`}
                  >
                    <div className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                      {c.email ? <span>{c.email}</span> : null}
                      {currentCompany ? (
                        <span className="text-amber-700">· aktuell bei {currentCompany}</span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-existing-cancel">Abbrechen</Button>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={handleAssign}
            disabled={!pickedId}
            data-testid="contact-existing-assign"
          >
            Zuordnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignConfirmDialog({ payload, onCancel, onConfirm }) {
  const open = !!payload;
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Person bereits zugeordnet
          </AlertDialogTitle>
          <AlertDialogDescription>
            {payload ? (
              <>
                Diese Person ist derzeit <strong>{payload.currentCompanyName}</strong> zugeordnet. Möchtest du
                {" "}<strong>{payload.contact.first_name} {payload.contact.last_name}</strong> zu dieser Firma verschieben?
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reassign-cancel" onClick={onCancel}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="reassign-confirm"
            onClick={onConfirm}
          >
            Zu dieser Firma verschieben
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

